import React, { useCallback, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { StyleSheet, TextInput, TextStyle } from 'react-native';

import WalletBetrBucksIcon from '@/assets/icons/wallet-betr-bucks';
import { SocialCurrencyActiveIcon } from '@/components/SocialCurrencyActiveIcon';
import { Text } from '@/components/TextComponent';
import { Box } from '@/components/lib/components';
import { ds } from '@/components/lib/design.system';
import { GASbkEvents } from '@/feature/analytics/sbk/ga-sbk-events';
import { useIsPredictionsProduct } from '@/hooks/use-is-predictions-product';
import { useIsCashCurrency } from '@/hooks/use-social-currency';
import { designSystem } from '@/styles/styles';
import { toSocialCurrency } from '@/utils/numeric/currency';

import { useIsBetSlipSubmitting, useIsOddsBetrBucks } from '../hooks/betslip-hooks';
import { useBetStakeLimit } from '../hooks/use-bet-stake-limit';
import { useSbkBetSlipStore } from '../hooks/use-sbk-betslip-store';
import { BetTypes } from '../types';
import { formatSocialStakeInput, sanitizeStakeInput } from '../utils/formatter';
import { useStakeInputValidation } from './betslip-num-pad/hooks/use-stake-input-validation';

const getDecimalHint = (displayStake: string): string => {
    if (!displayStake || displayStake === '0') {
        return '';
    }
    if (!displayStake.includes('.')) {
        return '.00';
    }
    const [, decimal] = displayStake.split('.');
    if (!decimal) {
        return '00';
    }
    if (decimal.length === 1) {
        return '0';
    }
    return '';
};

const CURSOR_GAP = 2;

const textStyle: TextStyle = {
    fontSize: 17,
    fontWeight: '400',
    letterSpacing: -0.43,
};

const styles = StyleSheet.create({
    valueRow: {
        flexShrink: 1,
    },
    input: {
        ...StyleSheet.absoluteFillObject,
        ...textStyle,
        padding: 0,
        paddingLeft: CURSOR_GAP,
        color: 'transparent',
        marginLeft: 20,
    },
    // Predictions render `$` inline (no leading icon), so the cursor only needs to clear the `$` glyph.
    inputPrediction: {
        marginLeft: 9,
    },
    valueText: {
        ...textStyle,
        color: ds.colorValue.text.primary,
    },
    hintText: {
        ...textStyle,
        marginLeft: CURSOR_GAP,
        color: designSystem.colors.neutral400,
    },
    placeholder: {
        ...textStyle,
        color: designSystem.colors.neutral300,
    },
    hiddenPlaceholder: {
        color: 'transparent',
    },
});

type StakeInputProps = {
    betId: string;
    hasError?: boolean;
};

export const StakeInput = ({ betId, hasError }: StakeInputProps) => {
    const displayStake = useSbkBetSlipStore(state => state.bets[betId]?.displayStake ?? '');
    const setEditingBet = useSbkBetSlipStore(state => state.actions.setEditingBet);
    const updateStake = useSbkBetSlipStore(state => state.actions.updateStake);
    const isFocused = useSbkBetSlipStore(state => state.editBetId === betId);
    const isBetSlipSubmitting = useIsBetSlipSubmitting();
    const isBetrCash = useIsCashCurrency();
    const isPredictions = useIsPredictionsProduct();
    // Cents/decimals are accepted for both Sweeps Cash and Predictions (USD).
    const acceptsDecimals = isBetrCash || isPredictions;
    const isBetrBucks = useIsOddsBetrBucks(betId);
    const { data: stakeLimit } = useBetStakeLimit(betId);
    const validateStakeInput = useStakeInputValidation();
    const promo = useSbkBetSlipStore(state => state.selectedPromos[betId]);
    const maxPromoStake = isBetrCash ? promo?.metadata.MAX_STAKE : undefined;

    useEffect(() => {
        if (!isNaN(Number(displayStake))) {
            validateStakeInput(betId, Number(displayStake), isBetrBucks, stakeLimit);
        }
    }, [stakeLimit, betId, isBetrBucks, displayStake, validateStakeInput]);

    const { t } = useTranslation('betslip_sbk');
    const label = isPredictions ? t('buy') : t('entry');
    const value = formatSocialStakeInput(displayStake);

    const handleChangeText = useCallback(
        (text: string) => {
            if (isBetSlipSubmitting) {
                return;
            }

            let nextStake = sanitizeStakeInput(text);

            // When backspace removes a comma, the sanitized value equals displayStake (no real change).
            // Detect this by checking if the only difference from the formatted value is a removed comma,
            // and remove the digit before the comma position instead.
            if (nextStake === displayStake && text.length === value.length - 1) {
                const commaIndex = value.split('').findIndex((ch, i) => ch !== text[i]);
                if (commaIndex >= 0 && value[commaIndex] === ',') {
                    const withoutDigitBeforeComma = text.slice(0, commaIndex - 1) + text.slice(commaIndex);
                    nextStake = sanitizeStakeInput(withoutDigitBeforeComma);
                }
            }

            if (nextStake === null || nextStake === displayStake) {
                return;
            }

            const nextStakeNumber = Number(nextStake);
            const betType = betId === 'COMBO' ? BetTypes.Combo : BetTypes.Single;
            updateStake(betId, nextStakeNumber, nextStake, betType);
        },
        [betId, displayStake, isBetSlipSubmitting, updateStake, value]
    );

    const handleFocus = useCallback(() => {
        GASbkEvents.clickWagerInput();
        setEditingBet(betId);
    }, [setEditingBet, betId]);

    const handleBlur = useCallback(() => {
        setEditingBet(null);
    }, [setEditingBet]);

    const hasValue = displayStake && displayStake !== '0';
    const decimalHint = getDecimalHint(displayStake);
    const selectionColor = hasError ? ds.colorValue.text.danger : ds.colorValue.text.brand;

    // Format the display value with full decimals when not focused (Betr Cash and Predictions both take cents)
    const decoratedValue = acceptsDecimals && !isFocused && hasValue ? `${value}${decimalHint}` : value;
    // Predictions render `$` inline since the SocialCurrencyActiveIcon yields null without a social wallet.
    const formattedValue = isPredictions ? `$${decoratedValue}` : decoratedValue;
    const showsValue = hasValue || isPredictions;

    return (
        <>
            <Text variant="bodySmall" letterSpacing={-0.08} color="neutral300">
                {label}
                {maxPromoStake ? ` ${t('max_promo_stake', { amount: toSocialCurrency(maxPromoStake) })}` : ''}
            </Text>
            <Box flexDirection="row" alignItems="center" gap="s4">
                {!isPredictions && isBetrBucks && isBetrCash ? (
                    <WalletBetrBucksIcon size={16} />
                ) : (
                    <SocialCurrencyActiveIcon />
                )}
                {showsValue ? (
                    <Text numberOfLines={1} style={styles.valueRow}>
                        <Text style={styles.valueText}>{formattedValue}</Text>
                        {decimalHint && acceptsDecimals && isFocused ? (
                            <Text style={styles.hintText}>{decimalHint}</Text>
                        ) : null}
                    </Text>
                ) : (
                    <Text numberOfLines={1} style={[styles.placeholder, isFocused && styles.hiddenPlaceholder]}>
                        {' '}
                    </Text>
                )}
                <TextInput
                    // Predictions auto-opens the slip with a fresh selection, ready for a buy amount
                    autoFocus={isPredictions}
                    keyboardType="decimal-pad"
                    keyboardAppearance="dark"
                    enterKeyHint="done"
                    autoCapitalize="none"
                    editable={!isBetSlipSubmitting}
                    onChangeText={handleChangeText}
                    onFocus={handleFocus}
                    onBlur={handleBlur}
                    value={hasValue ? value : ''}
                    style={[styles.input, isPredictions && styles.inputPrediction]}
                    selectionColor={selectionColor}
                />
            </Box>
        </>
    );
};
